import React, { useCallback, useEffect, useState } from 'react'
import Box from '@mui/joy/Box/index.js'
import Sheet from '@mui/joy/Sheet/index.js'
import List from '@mui/joy/List/index.js'
import ListItem from '@mui/joy/ListItem/index.js'
import ListItemButton from '@mui/joy/ListItemButton/index.js'
import ListItemContent from '@mui/joy/ListItemContent/index.js'
import Typography from '@mui/joy/Typography/index.js'
import Button from '@mui/joy/Button/index.js'
import Chip from '@mui/joy/Chip/index.js'
import CircularProgress from '@mui/joy/CircularProgress/index.js'
import Select from '@mui/joy/Select/index.js'
import Option from '@mui/joy/Option/index.js'
import ThumbUpIcon from '@mui/icons-material/ThumbUp'
import ThumbDownIcon from '@mui/icons-material/ThumbDown'
import HelpOutlineIcon from '@mui/icons-material/HelpOutline'
import MoreHorizIcon from '@mui/icons-material/MoreHoriz'
import dayjs from 'dayjs'
import { get } from 'aws-amplify/api'
import { fetchAuthSession } from 'aws-amplify/auth'

export default function ConversationReview() {
  const [conversations, setConversations] = useState([])
  const [selectedConversation, setSelectedConversation] = useState(null)
  const [loading, setLoading] = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)
  const [ratingFilter, setRatingFilter] = useState('all')
  const [nextToken, setNextToken] = useState(null)
  const [hasMore, setHasMore] = useState(true)
  const [conversationDetails, setConversationDetails] = useState(null)
  const [loadingDetails, setLoadingDetails] = useState(false)
  const [userDetails, setUserDetails] = useState(null)
  const [loadingStats, setLoadingStats] = useState({
    pagesScanned: 0,
    docsFound: 0,
  })

  const fetchConversations = useCallback(
    async (
      filter = 'all',
      token = null,
      append = false,
      accumulatedFiltered = [],
      targetCount = 20,
      pageCount = 0,
    ) => {
      if (token) {
        setLoadingMore(true)
      } else {
        setLoading(true)
        setLoadingStats({ pagesScanned: 0, docsFound: 0 })
      }

      const s = await fetchAuthSession()
      const idToken = s.tokens?.idToken?.toString()

      try {
        const queryParams = {
          limit: '20',
        }

        if (token) {
          queryParams.nextToken = token
        }

        // Fetch all conversations from all users for admin review
        const restOperation = get({
          apiName: 'technician-bot',
          path: '/1/conversations?username=all',
          options: {
            headers: {
              Authorization: idToken,
            },
            queryParams,
          },
        })

        const { body } = await restOperation.response
        const response = await body.json()

        const nextTokenFromResponse = response.nextToken || null
        const hasMorePages = !!response.nextToken
        const currentPageCount = pageCount + 1

        const mappedConversations = (response.conversations ?? [])
          .map((conversation) => ({
            id: conversation.conversationId,
            title: conversation.title,
            date: conversation.timestamp,
            userId: conversation.userId,
            message: conversation.firstMessage,
            userRating: conversation.userRating,
          }))
          .sort((a, b) => b.date - a.date)

        // Client-side filtering by rating (until backend supports it)
        const filteredConversations =
          filter === 'all' ? mappedConversations
          : filter === 'rated' ?
            mappedConversations.filter((c) => c.userRating === true)
          : filter === 'unrated' ?
            mappedConversations.filter((c) => c.userRating === false)
          : mappedConversations.filter(
              (c) => c.userRating === null || c.userRating === undefined,
            )

        // Accumulate filtered results
        const totalFiltered = [...accumulatedFiltered, ...filteredConversations]

        // Update loading stats
        setLoadingStats({
          pagesScanned: currentPageCount,
          docsFound: totalFiltered.length,
        })

        // If we have enough filtered results or no more pages, update state
        if (totalFiltered.length >= targetCount || !hasMorePages) {
          setNextToken(nextTokenFromResponse)
          setHasMore(hasMorePages)

          if (append) {
            setConversations((prev) => [...prev, ...totalFiltered])
          } else {
            setConversations(totalFiltered)
          }

          setLoading(false)
          setLoadingMore(false)
        } else {
          // Need more filtered results - fetch next page recursively
          await fetchConversations(
            filter,
            nextTokenFromResponse,
            append,
            totalFiltered,
            targetCount,
            currentPageCount,
          )
        }
      } catch (error) {
        console.error('Error fetching conversations:', error)
        setLoading(false)
        setLoadingMore(false)
      }
    },
    [],
  )

  const fetchUserDetails = async (userId) => {
    const s = await fetchAuthSession()
    const idToken = s.tokens?.idToken?.toString()

    try {
      const restOperation = get({
        apiName: 'technician-bot',
        path: `/1/users/${encodeURIComponent(userId)}`,
        options: {
          headers: {
            Authorization: idToken,
          },
        },
      })

      const { body } = await restOperation.response
      const response = await body.json()

      setUserDetails(response)
    } catch (error) {
      console.error('Error fetching user details:', error)
      setUserDetails(null)
    }
  }

  const fetchConversationDetails = async (conversationId, userId) => {
    setLoadingDetails(true)
    setUserDetails(null)
    const s = await fetchAuthSession()
    const idToken = s.tokens?.idToken?.toString()

    try {
      const path = `/1/conversations/${conversationId}?username=${encodeURIComponent(userId)}`

      const restOperation = get({
        apiName: 'technician-bot',
        path,
        options: {
          headers: {
            Authorization: idToken,
          },
        },
      })

      const { body } = await restOperation.response
      const response = await body.json()

      setConversationDetails(response)
      void fetchUserDetails(userId)
    } catch (error) {
      console.error('Error fetching conversation details:', error)
    } finally {
      setLoadingDetails(false)
    }
  }

  useEffect(() => {
    void fetchConversations(ratingFilter)
  }, [ratingFilter, fetchConversations])

  const loadMoreConversations = () => {
    if (nextToken && hasMore && !loadingMore) {
      void fetchConversations(ratingFilter, nextToken, true)
    }
  }

  const handleSelectConversation = (conversation) => {
    setSelectedConversation(conversation)
    void fetchConversationDetails(conversation.id, conversation.userId)
  }

  const handleScroll = (e) => {
    const bottom =
      e.target.scrollHeight - e.target.scrollTop <= e.target.clientHeight + 200
    if (bottom && hasMore && !loadingMore && !loading) {
      loadMoreConversations()
    }
  }

  const getRatingIcon = (rating) => {
    if (rating === true) {
      return <ThumbUpIcon sx={{ fontSize: '16px', color: 'success.500' }} />
    } else if (rating === false) {
      return <ThumbDownIcon sx={{ fontSize: '16px', color: 'danger.500' }} />
    }
    return <HelpOutlineIcon sx={{ fontSize: '16px', color: 'neutral.400' }} />
  }

  const getRatingColor = (rating) => {
    if (rating === true) return 'success'
    if (rating === false) return 'danger'
    return 'neutral'
  }

  return (
    <Box
      sx={{
        display: 'flex',
        height: '100%',
        width: '100%',
      }}
    >
      {/* Conversations List */}
      <Sheet
        sx={{
          width: '400px',
          borderRight: '1px solid',
          borderColor: 'divider',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Box
          sx={{
            p: 2,
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Typography level="title-md" sx={{ mb: 2 }}>
            All Conversations
          </Typography>
          <Select
            value={ratingFilter}
            onChange={(e, newValue) => {
              setRatingFilter(newValue)
            }}
            size="sm"
          >
            <Option value="all">All Ratings</Option>
            <Option value="rated">Thumbs Up</Option>
            <Option value="unrated">Thumbs Down</Option>
            <Option value="no-rating">No Rating</Option>
          </Select>
        </Box>

        <List
          sx={{
            flex: 1,
            overflowY: 'auto',
            p: 0,
          }}
          onScroll={handleScroll}
        >
          {loading ?
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <CircularProgress size="lg" />
              <Typography level="title-md" sx={{ mt: 2, mb: 1 }}>
                Loading across all users
              </Typography>
              <Typography
                level="body-sm"
                sx={{ color: 'text.secondary', mb: 2 }}
              >
                Thank you for your patience as we scan through the table
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  gap: 3,
                  mt: 2,
                }}
              >
                <Box>
                  <Typography level="h3" sx={{ color: 'primary.500' }}>
                    {loadingStats.pagesScanned}
                  </Typography>
                  <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
                    Pages Scanned
                  </Typography>
                </Box>
                <Box>
                  <Typography level="h3" sx={{ color: 'success.500' }}>
                    {loadingStats.docsFound}
                  </Typography>
                  <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
                    Matches Found
                  </Typography>
                </Box>
              </Box>
            </Box>
          : conversations.length === 0 ?
            <Box sx={{ p: 2, textAlign: 'center' }}>
              <Typography level="body-sm">No conversations found</Typography>
            </Box>
          : <>
              {conversations.map((conversation) => (
                <ListItem key={conversation.id}>
                  <ListItemButton
                    selected={selectedConversation?.id === conversation.id}
                    onClick={() => {
                      handleSelectConversation(conversation)
                    }}
                    sx={{
                      p: 2,
                      borderRadius: 0,
                      '&.Mui-selected': {
                        backgroundColor: 'primary.softBg',
                      },
                    }}
                  >
                    <ListItemContent>
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'flex-start',
                          mb: 0.5,
                        }}
                      >
                        <Typography level="title-sm" sx={{ flex: 1, pr: 1 }}>
                          {conversation.title}
                        </Typography>
                        <Chip
                          size="sm"
                          variant="soft"
                          color={getRatingColor(conversation.userRating)}
                          startDecorator={getRatingIcon(
                            conversation.userRating,
                          )}
                        >
                          {conversation.userRating === true ?
                            'Rated'
                          : conversation.userRating === false ?
                            'Unrated'
                          : 'None'}
                        </Chip>
                      </Box>
                      <Typography
                        level="body-xs"
                        sx={{ color: 'text.secondary', mb: 0.5 }}
                      >
                        {conversation.userId}
                      </Typography>
                      <Typography
                        level="body-xs"
                        sx={{ color: 'text.tertiary' }}
                      >
                        {dayjs(parseInt(conversation.date)).format(
                          'MMM D, YYYY h:mm A',
                        )}
                      </Typography>
                    </ListItemContent>
                  </ListItemButton>
                </ListItem>
              ))}

              {loadingMore && (
                <Box sx={{ p: 2, textAlign: 'center' }}>
                  <CircularProgress size="sm" />
                </Box>
              )}

              {!loadingMore && hasMore && (
                <Box sx={{ p: 2, textAlign: 'center' }}>
                  <Button
                    onClick={loadMoreConversations}
                    startDecorator={<MoreHorizIcon />}
                    size="sm"
                    variant="soft"
                  >
                    Load more
                  </Button>
                </Box>
              )}
            </>
          }
        </List>
      </Sheet>

      {/* Conversation Details */}
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {!selectedConversation ?
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Typography level="body-md" sx={{ color: 'text.secondary' }}>
              Select a conversation to view details
            </Typography>
          </Box>
        : loadingDetails ?
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <CircularProgress />
          </Box>
        : <Box
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <Sheet
              sx={{
                p: 2,
                borderBottom: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Typography level="title-lg">
                {selectedConversation.title}
              </Typography>
              <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
                {userDetails ?
                  <>
                    User: {userDetails.fullName || userDetails.username}
                    {userDetails.email && ` (${userDetails.email})`} •{' '}
                  </>
                : <>User: {selectedConversation.userId} • </>}
                {dayjs(parseInt(selectedConversation.date)).format(
                  'MMM D, YYYY h:mm A',
                )}
              </Typography>
            </Sheet>

            <Box
              sx={{
                flex: 1,
                overflowY: 'auto',
                p: 2,
              }}
            >
              {conversationDetails?.messages?.map((message, index) => (
                <Box
                  key={message.messageId || index}
                  sx={{
                    mb: 2,
                    p: 2,
                    borderRadius: 'sm',
                    backgroundColor:
                      message.author === 'bot' ?
                        'background.level1'
                      : 'primary.softBg',
                  }}
                >
                  <Typography level="title-sm" sx={{ mb: 1 }}>
                    {message.author === 'bot' ? 'Tech Assistant' : 'User'}
                  </Typography>
                  <Typography level="body-sm" sx={{ whiteSpace: 'pre-wrap' }}>
                    {message.message}
                  </Typography>
                  {message.context && message.context.length > 0 && (
                    <Box sx={{ mt: 2 }}>
                      <Typography
                        level="body-xs"
                        sx={{ fontWeight: 'bold', mb: 1 }}
                      >
                        References (
                        {message.context.reduce(
                          (acc, ctx) =>
                            acc + (ctx.retrievedReferences?.length || 0),
                          0,
                        )}
                        ):
                      </Typography>
                      {message.context.map((ctx, ctxIndex) =>
                        ctx.retrievedReferences?.map((ref, refIndex) => (
                          <Box
                            key={`${ctxIndex}-${refIndex}`}
                            sx={{
                              mt: 1,
                              p: 1,
                              borderRadius: 'xs',
                              backgroundColor: 'background.level2',
                              border: '1px solid',
                              borderColor: 'divider',
                            }}
                          >
                            <Typography
                              level="body-xs"
                              sx={{ fontWeight: 'bold' }}
                            >
                              {ref.metadata?.CacheTitle || 'Reference'}
                            </Typography>
                            {ref.metadata?.LitOrderNbr && (
                              <Typography
                                level="body-xs"
                                sx={{ color: 'text.tertiary' }}
                              >
                                {ref.metadata.LitOrderNbr}
                              </Typography>
                            )}
                          </Box>
                        )),
                      )}
                    </Box>
                  )}
                </Box>
              ))}
            </Box>
          </Box>
        }
      </Box>
    </Box>
  )
}
